// M17 P6 — Recruiter Profile: live monthly target + joining performance.
// Salary / Incentives / Attendance are explicit COMING SOON placeholders —
// parked scope, no data behind them by design.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BadgeIndianRupee, CalendarCheck2, ClipboardCheck, Gift, Mail, Phone, Target, TrendingUp, UserRound,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
};

const ComingSoonCard = ({ icon: Icon, title, description, testId }) => (
  <Card className="rounded-xl border-dashed border-border shadow-none bg-muted/30" data-testid={testId}>
    <CardContent className="p-5 flex items-start gap-3">
      <span className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Icon className="h-4.5 w-4.5 text-muted-foreground" />
      </span>
      <div className="min-w-0">
        <p className="font-medium flex items-center gap-2">
          {title}
          <Badge variant="outline" className="rounded-full text-[10px] bg-amber-50 text-amber-700 border-amber-200">Coming soon</Badge>
        </p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
    </CardContent>
  </Card>
);

export default function RecruiterProfile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/recruiter-flow/profile")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  const tm = data.this_month;
  const pct = tm.target > 0 ? Math.min(Math.round((tm.joined / tm.target) * 100), 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">My Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">Your target, live performance and account details.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* identity */}
        <Card className="rounded-xl border-border shadow-none" data-testid="profile-identity-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <span className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold">
                {(data.user.name || "?").charAt(0).toUpperCase()}
              </span>
              <div>
                <p className="font-heading font-semibold">{data.user.name}</p>
                <p className="text-xs text-muted-foreground">Recruiter</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono">{data.user.mobile}</span></p>
              {data.user.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{data.user.email}</p>}
              {data.user.joined_platform_at && (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <UserRound className="h-3.5 w-3.5" /> On the platform since {formatDate(data.user.joined_platform_at)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* target & performance */}
        <Card className="rounded-xl border-primary/30 shadow-none lg:col-span-2" data-testid="profile-target-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Target & performance — {monthLabel(tm.month)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border border-border p-3">
                <p className="text-2xl font-heading font-semibold" data-testid="profile-target-value">{tm.target || "—"}</p>
                <p className="text-xs text-muted-foreground">Monthly target</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-2xl font-heading font-semibold text-emerald-600" data-testid="profile-joined-value">{tm.joined}</p>
                <p className="text-xs text-muted-foreground">Joined this month</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-2xl font-heading font-semibold text-amber-600" data-testid="profile-gap-value">{tm.gap}</p>
                <p className="text-xs text-muted-foreground">Gap to target</p>
              </div>
            </div>
            {tm.target > 0 ? (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progress</span><span>{pct}%</span>
                </div>
                <Progress value={pct} className="h-2" data-testid="profile-target-progress" />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="profile-no-target-note">
                No target set for this month yet — your admin sets monthly joining targets.
              </p>
            )}
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Month</th>
                    <th className="text-right px-3 py-2 font-medium">Target</th>
                    <th className="text-right px-3 py-2 font-medium">Joined</th>
                    <th className="text-right px-3 py-2 font-medium">Gap</th>
                  </tr>
                </thead>
                <tbody data-testid="profile-months-table">
                  {data.recent_months.map((m) => (
                    <tr key={m.month} className="border-t border-border">
                      <td className="px-3 py-2">{monthLabel(m.month)}</td>
                      <td className="px-3 py-2 text-right font-mono">{m.target || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700">{m.joined}</td>
                      <td className="px-3 py-2 text-right font-mono">{m.target ? m.gap : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> {data.totals.total_joined} total joinings ·{" "}
              <ClipboardCheck className="h-3.5 w-3.5" />
              <Link to="/recruiter/selection" className="text-primary hover:underline">{data.totals.selection_backlog} awaiting admin decision</Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* coming soon placeholders (parked scope) */}
      <div className="space-y-3">
        <h2 className="text-base font-heading font-semibold">More on the way</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <ComingSoonCard icon={BadgeIndianRupee} title="Salary" testId="coming-soon-salary"
            description="Your salary summary will appear here once payroll goes live." />
          <ComingSoonCard icon={Gift} title="Incentives" testId="coming-soon-incentives"
            description="Joining-based incentive calculations are planned for a future release." />
          <ComingSoonCard icon={CalendarCheck2} title="Attendance" testId="coming-soon-attendance"
            description="Attendance tracking will be added here later." />
        </div>
      </div>
    </div>
  );
}
